// The one confirmation notice for the app. Rendered through a portal onto
// document.body so it sits above every modal overlay and side panel — a
// toast drawn inside a page ends up dimmed behind the overlay it is
// confirming, which is how "Reminder sent" became unreadable. Solid
// background, white text, high z-index; sits above the bottom nav on mobile.
//
//   const { toast, showToast } = useToast();
//   showToast('Saved.');                 // success (default)
//   showToast('Failed: …', 'error');
//   showToast('Heads up', 'warn');
//   …
//   <Toast {...toast} />
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useIsMobile from '../hooks/useIsMobile';

const TONE = {
  success: { bg: 'var(--success)', icon: '✓' },
  error:   { bg: 'var(--danger)',  icon: '!' },
  warn:    { bg: 'var(--warn)',    icon: '!' },
  info:    { bg: 'var(--accent)',  icon: 'i' },
};

export function useToast(defaultMs = 5000) {
  const [toast, setToast] = useState(null);
  const timer = useRef(null);
  const showToast = useCallback((message, type = 'success', ms = defaultMs) => {
    clearTimeout(timer.current);
    setToast({ message, type: TONE[type] ? type : 'success' });
    timer.current = setTimeout(() => setToast(null), ms);
  }, [defaultMs]);
  const hideToast = useCallback(() => { clearTimeout(timer.current); setToast(null); }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return { toast, showToast, hideToast };
}

export default function Toast({ message, type = 'success', onClose }) {
  const isMobile = useIsMobile();
  if (!message) return null;
  const t = TONE[type] || TONE.success;
  return createPortal(
    <div role="status" aria-live="polite" onClick={onClose}
      style={{ position: 'fixed', right: isMobile ? 12 : 24, left: isMobile ? 12 : 'auto', bottom: isMobile ? 76 : 24, zIndex: 20000,
        display: 'flex', alignItems: 'flex-start', gap: 10, maxWidth: isMobile ? 'none' : 440,
        background: t.bg, color: 'var(--white)', borderRadius: 10, padding: '12px 16px', fontSize: 13.5, fontWeight: 500, lineHeight: 1.45,
        boxShadow: '0 8px 28px rgba(0,0,0,0.28)', cursor: onClose ? 'pointer' : 'default' }}>
      <span aria-hidden style={{ flexShrink: 0, width: 20, height: 20, borderRadius: 10, background: 'rgba(255,255,255,0.22)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>{t.icon}</span>
      <span>{message}</span>
    </div>,
    document.body,
  );
}
